import type { GridContext } from '@/types/grid'
import { readCanonicalRowFieldValue } from '@/components/Grid/utils/grid.utils'
import {
  extractIdLike,
  mustPersistOnlyInProperties,
} from '@/domain/product/persistence/propertyRules'
import { readPicturesFromRow } from '@/domain/product/pictures'
import { resolveRefDisplayEntries } from './refDataDisplay'

type GroupLeafRow = Record<string, unknown> & {
  name?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const normalizeKey = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const getSupportedFieldType = (
  context: GridContext | undefined,
  key: string,
): string | undefined => {
  return context?.supportedKeys?.find(
    (supportedKey) => supportedKey.key === key,
  )?.type
}

const getDisplayLookup = (
  context: GridContext | undefined,
  key: string,
): Record<string, string> => {
  const supportedKey = context?.supportedKeys?.find(
    (candidate) => candidate.key === key,
  )

  if (
    supportedKey?.isRef &&
    mustPersistOnlyInProperties({
      key,
      type: supportedKey.type,
    })
  ) {
    return context?.referenceLabelsById ?? {}
  }

  return context?.refDataByKey?.[key] ?? {}
}

const toDisplayValues = (
  rawValue: unknown,
  key: string,
  context: GridContext | undefined,
  row?: GroupLeafRow,
): string[] => {
  const lookup = getDisplayLookup(context, key)

  return resolveRefDisplayEntries({
    value: rawValue,
    data: row,
    field: key,
    fieldType: getSupportedFieldType(context, key),
    refData: lookup,
    context,
  })
    .map((entry) => normalizeKey(entry.label))
    .filter((value): value is string => value !== null)
}

const resolveRefFieldKey = (
  context: GridContext | undefined,
  _row: GroupLeafRow | undefined,
): string | null => {
  const contextRefKey = normalizeKey(context?.refKey)
  if (contextRefKey) return contextRefKey

  const supportedRefKey = context?.supportedKeys?.find((key) => key.isRef)?.key
  const normalizedSupportedRefKey = normalizeKey(supportedRefKey)
  if (normalizedSupportedRefKey) return normalizedSupportedRefKey

  return null
}

const getRowFieldValue = (
  row: GroupLeafRow | undefined,
  key: string,
  context: GridContext | undefined,
): unknown => {
  return readCanonicalRowFieldValue(
    row,
    key,
    getSupportedFieldType(context, key),
  )
}

export const resolveBatchGroupLabel = ({
  context,
  firstLeafData,
  fallbackLabel,
}: {
  context?: GridContext
  firstLeafData?: unknown
  fallbackLabel: string
}): string => {
  const row = isRecord(firstLeafData)
    ? (firstLeafData as GroupLeafRow)
    : undefined

  const refFieldKey = resolveRefFieldKey(context, row)
  if (refFieldKey) {
    const refValues = toDisplayValues(
      getRowFieldValue(row, refFieldKey, context),
      refFieldKey,
      context,
      row,
    )
    if (refValues.length > 0) return refValues[0]
  }

  const nameValues = toDisplayValues(
    getRowFieldValue(row, 'name', context),
    'name',
    context,
    row,
  )
  if (nameValues.length > 0) return nameValues[0]

  const hasDeletedValue = [...new Set([refFieldKey, 'name'])].some((key) => {
    if (!key) return false
    const raw = getRowFieldValue(row, key, context)
    return (Array.isArray(raw) ? raw : [raw]).some((item) =>
      context?.deletedOptionIdsByKey?.[key]?.includes(
        extractIdLike(item) ?? String(item),
      ),
    )
  })
  if (hasDeletedValue) return 'Occurrences'

  const fallback = normalizeKey(fallbackLabel)
  return fallback ?? 'Occurrences'
}

export const resolveBatchGroupPictureValue = ({
  aggregatedValue,
  firstLeafData,
}: {
  aggregatedValue: unknown
  firstLeafData?: unknown
}): string[] | undefined => {
  const toPicturesArray = (value: unknown): string[] | undefined => {
    if (Array.isArray(value)) {
      const pictures = value.filter(
        (entry): entry is string => typeof entry === 'string',
      )
      return pictures.length > 0 ? pictures : undefined
    }

    if (typeof value === 'string') {
      return value.length > 0 ? [value] : undefined
    }

    return undefined
  }

  const aggregatedPictures = toPicturesArray(aggregatedValue)

  if (aggregatedPictures) {
    return aggregatedPictures
  }

  const row = isRecord(firstLeafData)
    ? (firstLeafData as GroupLeafRow)
    : undefined

  return toPicturesArray(
    readPicturesFromRow({
      properties: Array.isArray(row?.properties) ? row.properties : undefined,
      pictures: readCanonicalRowFieldValue(row, 'pictures', 'LIST'),
    }),
  )
}
